import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { toast } from 'sonner';

import { getServerErrorMessage, isConflictError } from './errors';
import { QUIET_TOAST } from './hubToast';
import type { GtSaveRequest } from './types';

/**
 * The slice of a React Query `UseMutationResult` the grid editor actually
 * touches. Kept structural (rather than the full `UseMutationResult`) so a
 * concrete mutation — `UseMutationResult<GtRow[], Error, GtRowPayload[]>`, etc.
 * — assigns cleanly without the `TData`/`TError` variance friction that the
 * callback params introduce. The callers don't read the success data and route
 * the error through {@link getServerErrorMessage} (which accepts `unknown`).
 */
export interface GridSaveMutation<TPayload> {
    isPending: boolean;
    mutate: (
        request: GtSaveRequest<TPayload>,
        options?: {
            onSuccess?: (saved: readonly SavedAnswer[]) => void;
            onError?: (error: unknown) => void;
        },
    ) => void;
}

/** The part of a saved answer the version is read from. */
interface SavedAnswer {
    id: number;
}

/** Version of a saved answer set, as the server computes it: the highest id, 0 when empty. */
export const answerSetVersion = (saved: readonly SavedAnswer[]): number =>
    saved.reduce((highest, answer) => Math.max(highest, answer.id), 0);

/**
 * The result a `validate` fn returns: the human-readable errors AND the
 * server-ready payload parsed out of the drafts.
 *
 * Each layer editor validates differently (page ranges, sheet indexes, line
 * items) and produces a different request body, so validation is supplied by
 * the caller. `errors` gates the save; `payload` is what gets sent. Keeping
 * both behind one memoised pass means the drafts are only walked once.
 */
export interface GridValidation<TPayload> {
    errors: string[];
    payload: TPayload;
}

/**
 * Anything the grid can edit: a draft just has to carry a stable React `key`
 * (used for add/update/remove identity, never sent to the server).
 */
interface DraftLike {
    key: string;
}

export interface UseGridEditorArgs<
    TDraft extends DraftLike,
    TPayload extends readonly unknown[],
    TExisting,
> {
    /** How many answers are saved now; an empty save over them needs a confirmed clear. */
    savedCount?: number;
    /**
     * `answerSetVersion` of the saved answers now loaded. The version the drafts were
     * seeded from goes with each save, so a save over someone else's newer save is refused.
     */
    savedVersion?: number;
    /** Refetch the saved answers; the conflict notice's Reload calls it and re-seeds the drafts. */
    reload?: () => Promise<{ isError: boolean }>;
    /** The query data the drafts are seeded from; drafts reset when it changes. */
    existing: TExisting;
    /**
     * Build the draft list from the freshly-loaded server data (or seed an
     * empty one). Called whenever `existing` becomes non-nullish or changes —
     * its result replaces the current drafts. Seeding policy (e.g. "show one
     * blank row when the list is empty") is the editor's to decide, so it
     * lives here.
     */
    seed: (existing: NonNullable<TExisting>) => TDraft[];
    /**
     * Whether the rows `seed` returns are a suggestion rather than saved data.
     * When true the dirty baseline is empty, so `isDirty` starts true (the rows
     * still need saving); `hasEdits` stays false until the person changes them.
     */
    seedIsUnsaved?: (existing: NonNullable<TExisting>) => boolean;
    /** Walk the drafts once: collect blocking errors AND the request payload. */
    validate: (drafts: TDraft[]) => GridValidation<TPayload>;
    /**
     * The React Query mutation the save flow drives — typically the result of a
     * `useSaveGt*` hook. Typed structurally (just `mutate` + `isPending`) so any
     * concrete `UseMutationResult<TData, TError, TPayload>` flows in without the
     * `TData`/`TError` variance friction a bare `unknown` would cause.
     */
    save: GridSaveMutation<TPayload>;
    /** Toast shown on a successful save (e.g. "Rows saved"). */
    successMessage: string;
    /**
     * Plural, lower-case entity name woven into server-error toasts via
     * {@link getServerErrorMessage} (e.g. "rows", "page splits", "sheets").
     */
    entityType: string;
}

export interface UseGridEditor<TDraft extends DraftLike> {
    /** Current editable rows. */
    drafts: TDraft[];
    /** Replace rows directly (escape hatch for editor-specific updates). */
    setDrafts: React.Dispatch<React.SetStateAction<TDraft[]>>;
    /**
     * Merge a partial patch into the row with `key`. Stable identity. The patch
     * may be a plain object or an updater `(prev) => Partial<TDraft>` for edits
     * that depend on the row's current value (e.g. a single nested field).
     */
    updateDraft: (
        key: string,
        patch: Partial<TDraft> | ((prev: TDraft) => Partial<TDraft>),
    ) => void;
    /** Drop the row with `key`. Stable identity (safe as a memo dependency). */
    removeDraft: (key: string) => void;
    /** Append a row produced by `factory`. */
    addDraft: (factory: () => TDraft) => void;
    /** Memoised blocking validation errors (empty ⇒ save allowed). */
    errors: string[];
    /** Whether there are unsaved differences vs. the last seeded state; drives save state, not the leave guard. */
    isDirty: boolean;
    /** Whether the rows differ from what loaded (or was last saved); a pre-fill alone is not an edit. Use this for the leave guard. */
    hasEdits: boolean;
    /** Saved answers the next save would delete (the payload is empty); 0 otherwise. */
    clearCount: number;
    /**
     * Validate, then save: bails with the first error toast if invalid,
     * otherwise fires the mutation and toasts success / server-error.
     */
    onSave: (options?: { confirmClear?: boolean }) => void;
    /** Why the last save was refused because someone else saved first; null otherwise. */
    conflict: string | null;
    /** Drop the drafts and load the saved answers again, clearing `conflict`. */
    reloadSaved: () => void;
}

/**
 * Shared "draft grid" skeleton for the GT layer editors.
 *
 * The three editors (`PageSplitEditor`, `SheetSplitEditor`, `RowEditor`) all
 * keep a local list of draft rows seeded from server data, let the operator
 * add/update/remove rows, validate the whole list on every change, and save the
 * parsed payload with toast feedback. This hook owns that skeleton; each editor
 * supplies only its domain-specific `seed`, `validate`, and empty-row factory.
 *
 * Behaviour is identical to the hand-rolled versions: drafts re-seed when
 * `existing` changes, validation is memoised over `drafts`, and `onSave` toasts
 * the first error (or fires the mutation with success / server-error toasts).
 */
export function useGridEditor<
    TDraft extends DraftLike,
    TPayload extends readonly unknown[],
    TExisting,
>({
    savedCount = 0,
    savedVersion,
    reload,
    existing,
    seed,
    seedIsUnsaved,
    validate,
    save,
    successMessage,
    entityType,
}: UseGridEditorArgs<TDraft, TPayload, TExisting>): UseGridEditor<TDraft> {
    const [drafts, setDrafts] = useState<TDraft[]>([]);

    // Snapshot of the last-seeded drafts so we can tell whether the operator
    // has made unsaved edits (dirty tracking). A ref (not state) keeps it out
    // of the render cycle — comparing against it is cheap.
    const seededRef = useRef<TDraft[]>([]);
    // Rows as loaded or last saved, even when they are only an unsaved suggestion.
    const loadedRef = useRef<TDraft[]>([]);

    // Mirrors `drafts` so the re-seed effect below can read the LATEST value
    // without adding `drafts` to its dependency array (which would re-fire the
    // effect — and re-seed — on every keystroke instead of only when `existing`
    // changes).
    const draftsRef = useRef<TDraft[]>([]);
    draftsRef.current = drafts;

    // `seed` is typically an inline closure (new identity each render); pin it
    // in a ref so re-seeding only fires when `existing` actually changes —
    // matching the original editors' `[existing]`-only effect dependency.
    const seedRef = useRef(seed);
    seedRef.current = seed;
    const seedIsUnsavedRef = useRef(seedIsUnsaved);
    seedIsUnsavedRef.current = seedIsUnsaved;
    const savedVersionRef = useRef(savedVersion);
    savedVersionRef.current = savedVersion;

    // The version of the saved answers the drafts were seeded from, or last saved as.
    const baseVersionRef = useRef<number | undefined>(undefined);
    // Set by Reload so the next `existing` re-seeds even over unsaved edits.
    const forceReseedRef = useRef(false);
    // Bumped by a successful Reload so drafts re-seed even when the refetch returns the same data.
    const [reloadCount, setReloadCount] = useState(0);
    const [conflict, setConflict] = useState<string | null>(null);

    useEffect(() => {
        if (existing == null) return;

        // Every save mutation invalidates the query feeding `existing`, so this
        // effect also fires on the refetch right after a successful save. If the
        // operator has made further edits since that save (or since the last
        // seed) in the window before the refetch lands, re-seeding here would
        // silently discard them. Skip the re-seed while dirty — the next save
        // (or a subsequent `existing` change once the operator is caught up)
        // will re-seed normally.
        const current = draftsRef.current;
        const isCurrentlyDirty =
            current.length !== seededRef.current.length ||
            JSON.stringify(current) !== JSON.stringify(seededRef.current);
        if (isCurrentlyDirty && !forceReseedRef.current) return;
        forceReseedRef.current = false;

        const source = existing as NonNullable<TExisting>;
        baseVersionRef.current = savedVersionRef.current;
        const seeded = seedRef.current(source);
        seededRef.current = seedIsUnsavedRef.current?.(source) ? [] : seeded;
        loadedRef.current = seeded;
        setDrafts(seeded);
    }, [existing, reloadCount]);

    const updateDraft = useCallback(
        (
            key: string,
            patch: Partial<TDraft> | ((prev: TDraft) => Partial<TDraft>),
        ) =>
            setDrafts((rows) =>
                rows.map((r) =>
                    r.key === key
                        ? {
                              ...r,
                              ...(typeof patch === 'function'
                                  ? patch(r)
                                  : patch),
                          }
                        : r,
                ),
            ),
        [],
    );

    const removeDraft = useCallback(
        (key: string) => setDrafts((rows) => rows.filter((r) => r.key !== key)),
        [],
    );

    const addDraft = useCallback(
        (factory: () => TDraft) => setDrafts((rows) => [...rows, factory()]),
        [],
    );

    const { errors, payload } = useMemo(
        () => validate(drafts),
        [drafts, validate],
    );

    const isDirty = useMemo(() => {
        // Cheap length short-circuit before the (already memoised-on-`drafts`)
        // deep compare: add/remove changes the row count, so we skip stringify
        // entirely in that common case.
        const seeded = seededRef.current;
        if (drafts.length !== seeded.length) return true;
        return JSON.stringify(drafts) !== JSON.stringify(seeded);
    }, [drafts]);

    // The error toast a refused save raised, closed once the problem it names is gone.
    const errorToastRef = useRef<{
        id: string | number;
        message: string;
    } | null>(null);
    useEffect(() => {
        const shown = errorToastRef.current;
        if (shown && !errors.includes(shown.message)) {
            toast.dismiss(shown.id);
            errorToastRef.current = null;
        }
    }, [errors]);

    const hasEdits = useMemo(() => {
        const loaded = loadedRef.current;
        if (drafts.length !== loaded.length) return true;
        return JSON.stringify(drafts) !== JSON.stringify(loaded);
    }, [drafts]);

    const clearCount = payload.length === 0 ? savedCount : 0;

    const onSave = useCallback(
        (options?: { confirmClear?: boolean }) => {
            if (errors.length > 0) {
                const id = toast.error(errors[0]);
                if (id !== undefined)
                    errorToastRef.current = { id, message: errors[0] };
                return;
            }
            const savedDrafts = drafts;
            save.mutate(
                {
                    payload,
                    confirmClear: options?.confirmClear,
                    baseVersion: baseVersionRef.current,
                },
                {
                    onSuccess: (saved) => {
                        // Saved rows become the new baseline; the identity-only update
                        // recomputes `isDirty` while keeping any edits made mid-save.
                        seededRef.current = savedDrafts;
                        loadedRef.current = savedDrafts;
                        baseVersionRef.current = answerSetVersion(saved);
                        setDrafts((rows) => [...rows]);
                        toast.success(successMessage, QUIET_TOAST);
                    },
                    onError: (err) =>
                        isConflictError(err)
                            ? setConflict(
                                  getServerErrorMessage(err, entityType),
                              )
                            : toast.error(
                                  getServerErrorMessage(err, entityType),
                              ),
                },
            );
        },
        [errors, payload, drafts, save, successMessage, entityType],
    );

    const reloadSaved = useCallback(() => {
        if (!reload) return;
        void reload().then(
            ({ isError }) => {
                if (isError) {
                    toast.error(`Failed to reload the ${entityType}.`);
                    return;
                }
                forceReseedRef.current = true;
                setConflict(null);
                setReloadCount((count) => count + 1);
            },
            () => toast.error(`Failed to reload the ${entityType}.`),
        );
    }, [reload, entityType]);

    return {
        drafts,
        setDrafts,
        updateDraft,
        removeDraft,
        addDraft,
        errors,
        isDirty,
        hasEdits,
        clearCount,
        onSave,
        conflict,
        reloadSaved,
    };
}
